/**
 * GenovaOrderModal.tsx
 *
 * Thin LabOrderModalCore adapter for Genova Diagnostics. Drop-ship model:
 * Genova ships the collection pack to the patient (required address), and the
 * order is sent as an HL7 v2.4 message server-side. The product catalog is
 * large, so staff search it live (GET /integrations/genova/products?search=).
 * The selected product id rides in metadata.genovaProductId; the order line
 * item uses the integration's default catalog product.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Search, X } from 'lucide-react';

import {
  createIntegrationOrder,
  getGenovaProducts,
  type EnabledIntegration,
  type CreateIntegrationOrderResult,
  type GenovaProduct,
} from '@/services/integrations.service';
import { updatePatient } from '@/services/patients.service';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult } from './LabOrderModalCore';

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

interface GenovaOrderModalProps {
  patientId: number;
  integration: EnabledIntegration;
  isOpen: boolean;
  onClose: () => void;
  /** All orders created this submit — one per selected test (multi-select). */
  onOrdered: (
    created: Array<{ result: CreateIntegrationOrderResult; testName: string }>,
    integrationProductId: number | null,
  ) => void;
}

export function GenovaOrderModal({ patientId, integration, isOpen, onClose, onOrdered }: GenovaOrderModalProps) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  // Multi-select: full product objects are kept (search results change under us).
  const [selected, setSelected] = useState<GenovaProduct[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { if (!isOpen) { setSearch(''); setDebounced(''); setSelected([]); } }, [isOpen]);

  const { data, isLoading } = useQuery({
    queryKey: ['genova-products', debounced],
    queryFn: () => getGenovaProducts(debounced || undefined),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });
  const products = data?.products ?? [];

  const isPicked = (id: number) => selected.some((s) => s.id === id);
  const toggle = (p: GenovaProduct) =>
    setSelected((prev) => (prev.some((s) => s.id === p.id) ? prev.filter((s) => s.id !== p.id) : [...prev, p]));
  const knownTotal = selected.reduce((sum, p) => sum + (p.price ?? 0), 0);
  const unpriced = selected.filter((p) => p.price == null);

  // Order line item uses the integration's default catalog product; the real
  // Genova product id travels in metadata.
  const lineProductId = integration.products?.[0]?.integrationProductId ?? null;

  const selectedRef = useRef(selected); selectedRef.current = selected;
  const lineProductIdRef = useRef(lineProductId); lineProductIdRef.current = lineProductId;
  const onOrderedRef = useRef(onOrdered); onOrderedRef.current = onOrdered;

  const config = useMemo<LabOrderConfig>(() => ({
    vendor: 'genova',
    title: 'Order Genova Test',
    requireAddress: true,
    // Drop-ship kit: no lab notes (PID notes / patient comments / order notes),
    // no collection date at order time, no vitals — same shape as SiPhox.
    features: { presets: false, facilityProvider: false, billing: false, diagnoses: false, tests: false, notes: false, collectionDate: false, vitals: false },
    submit: async (payload): Promise<LabOrderSubmitResult> => {
      const picked = selectedRef.current;
      if (picked.length === 0) throw new Error('Please select at least one test.');

      // A lab order may FILL a missing demographic, never change one — the
      // patient profile is the only place demographics are edited. The allowed
      // set and the was-it-blank test live in one place,
      // LabOrderModalCore.buildDemographicsFill(); name, e-mail and phone are
      // deliberately excluded.
      if (payload.demographicsFill) {
        await updatePatient(payload.patientId, payload.demographicsFill);
      }

      // One Genova order PER selected test: each test is a separate HL7
      // drop-ship order on Genova's side (their EMR order carries a single
      // product), and each gets its own cash price, status polling, and
      // cancel window here.
      const pid = lineProductIdRef.current;
      const lines: string[] = [];
      const created: Array<{ result: CreateIntegrationOrderResult; testName: string }> = [];
      for (const [i, prod] of picked.entries()) {
        try {
          const result = await createIntegrationOrder(payload.patientId, {
            key: 'genova',
            items: pid != null ? [{ integrationProductId: pid, quantity: 1 }] : undefined,
            metadata: {
              genovaProductId: prod.id,
              genovaProductName: prod.name,
              // Display/back-compat only — the server re-derives the authoritative
              // cash price from its fee schedule when setting the order amount.
              genovaCashPrice: prod.price ?? undefined,
              shippingAddress: {
                recipientName: payload.recipientName || undefined,
                line1: payload.street,
                line2: payload.street2 || undefined,
                city: payload.city,
                state: payload.state,
                zip: payload.zip,
                country: payload.country || undefined,
                phone: payload.phone || undefined,
                patientShippingAddressId: payload.shippingAddressId ?? undefined,
              },
            },
          });
          created.push({ result, testName: prod.name });
          lines.push(`Order #${result.integrationOrderId} — ${prod.name}`);
        } catch (err) {
          // Stop at the first failure; report what was created vs. not ordered.
          const remaining = picked.slice(i).map((p) => p.name).join(', ');
          const msg = `Failed at "${prod.name}" — ${err instanceof Error ? err.message : 'order error'}. Not ordered: ${remaining}.`;
          if (created.length === 0) return { ok: false, lines: [], errorMessage: msg };
          onOrderedRef.current(created, pid);
          return { ok: true, lines: [...lines, `⚠ ${msg}`] };
        }
      }
      onOrderedRef.current(created, pid);
      return { ok: true, lines: [`${lines.length} Genova ${lines.length === 1 ? 'test' : 'tests'} ordered:`, ...lines] };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const topSlot = (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
      Genova ships the collection kit to the patient. The order is sent to Genova on submit.
    </div>
  );

  const itemsSlot = (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="text-sm font-semibold text-slate-700">Tests</span>
        {selected.length > 0 && (
          <span className="text-xs font-medium text-emerald-700">
            {selected.length} selected{knownTotal > 0 ? ` · ${usd(knownTotal)}${unpriced.length ? '+' : ''}` : ''}
          </span>
        )}
      </div>
      <div className="space-y-2 px-4 py-3">
        {/* Selected tests — each removable; each order ships its own kit. */}
        {selected.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2">
            <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-900">
              <Check className="h-4 w-4 flex-shrink-0" /> {p.name}
              {p.price != null && <span className="font-semibold">· {usd(p.price)}</span>}
            </span>
            <button
              type="button"
              onClick={() => toggle(p)}
              className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900"
              aria-label={`Remove ${p.name}`}
            >
              <X className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        ))}
        {selected.length > 1 && (
          <p className="text-[11px] text-slate-500">
            Each test is placed as its own Genova order (separate kit, tracking, and charge).
          </p>
        )}

        {/* Search + pick — always visible so more tests can be added. */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={selected.length ? 'Add another test…' : 'Search Genova tests (e.g. thyroid, adrenal, GI)…'}
            className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {isLoading ? (
          <p className="text-xs italic text-slate-400">Loading Genova catalog…</p>
        ) : products.length > 0 ? (
          <div className="max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
            {products.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50 ${isPicked(p.id) ? 'bg-emerald-50/60 text-emerald-900' : 'text-slate-700'}`}
              >
                <span className="flex items-center gap-1.5">
                  {isPicked(p.id) && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
                  {p.name}
                </span>
                {p.price != null && (
                  <span className="shrink-0 text-xs font-medium text-slate-500">{usd(p.price)}</span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-amber-700">
            {data?.configured === false
              ? 'Genova is not configured for this practice yet (missing credentials).'
              : (data?.error || (debounced ? 'No tests match your search.' : 'No tests available from Genova right now.'))}
          </p>
        )}
      </div>
    </div>
  );

  const confirmSlot = selected.length > 0 ? (
    <div className="space-y-1">
      <p className="font-semibold text-blue-900">{selected.length === 1 ? 'Test:' : `Tests (${selected.length} — one order each):`}</p>
      <ul className="ml-5 list-disc space-y-0.5">
        {selected.map((p) => (
          <li key={p.id} className="text-blue-800 text-sm">
            {p.name}
            {p.price != null && <span className="ml-2 font-semibold">{usd(p.price)}</span>}
          </li>
        ))}
      </ul>
      {knownTotal > 0 && (
        <p className="font-semibold text-blue-900">Total: {usd(knownTotal)}{unpriced.length ? ' + unpriced tests' : ''}</p>
      )}
      {unpriced.length > 0 && (
        <p className="text-xs italic text-blue-700">
          No fee-schedule price matched for {unpriced.map((p) => p.name).join(', ')} — the account's configured Genova price applies to those.
        </p>
      )}
    </div>
  ) : null;

  return (
    <LabOrderModalCore
      config={config}
      patientId={patientId}
      open={isOpen}
      onClose={onClose}
      onOrdered={() => { /* handled by parent via onOrdered */ }}
      topSlot={topSlot}
      itemsSlot={itemsSlot}
      confirmSlot={confirmSlot}
    />
  );
}
